import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react';
import clsx from 'clsx';
import useLoginStore from 'components/login/useLoginStore';
import { convertBase64ToFile, convertFileToBase64 } from 'helpers/file.helper';
import {
  Modal,
  OpImageUploader,
  OpSelect,
  OpTextBox,
} from 'op2mise-react-widgets';
import Switch from 'shared/components/switch/Switch';
import BootstrapSpinner from 'shared/components/bootstrap-spinner/BootstrapSpinner';
import {
  getProfileGeneralDataAsync,
  updateProfileGeneralDataAsync,
} from 'services/user-profile';
import { PhoneLocationOptions, PhoneTypeOptions } from './generalForm.constant';
import { validateGeneralForm } from './generalForm.helper';
import styles from './GeneralForm.module.css';

export const GeneralForm = forwardRef(({ setIsDirty }, ref) => {
  const { setProfilePicture, setUsername, setJobTitle } = useLoginStore(
    (state) => state,
  );

  const phoneLocationFields = { text: 'name', value: 'name' };
  const phoneTypeFields = { text: 'name', value: 'name' };
  const [data, setData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    jobTitle: '',
    orderContactRole: '',
    phoneLocation: PhoneLocationOptions[0].name,
    phoneType: PhoneTypeOptions[0].name,
    areaCode: '',
    phoneNumber: '',
    isEmailNotificationEnabled: true,
    profilePhoto: null,
    fileExtension: '',
    fileChanged: false,
  });

  const [pristineData, setPristineData] = useState(null);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        const result = await getProfileGeneralDataAsync();

        if (result) {
          const defaultProfilePhotoName =
            result.profilePhotoName || 'profile-photo';
          const photoFile = convertBase64ToFile(
            result.profilePhoto,
            defaultProfilePhotoName,
          );
          const initialData = {
            ...result,
            phoneLocation: result.phoneLocation ?? data.phoneLocation,
            phoneType: result.phoneType ?? data.phoneType,
            profilePhoto: photoFile,
          };

          console.log(initialData);

          setData(initialData);
          setPristineData(initialData);
        }
      } catch (error) {
        console.error('Failed to fetch profile general data', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  useImperativeHandle(ref, () => ({
    save: async () => {
      setApiError('');
      if (!validateGeneralForm(data, setErrors)) return false;

      try {
        let base64Photo = '';
        let fileExt = '';

        if (data.fileChanged) {
          base64Photo = await convertFileToBase64(data.profilePhoto);
          fileExt = data.fileExtension || '';
        }

        const payloadToSave = {
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          jobTitle: data.jobTitle,
          orderContactRole: data.orderContactRole,
          phoneLocation: data.phoneLocation,
          phoneType: data.phoneType,
          areaCode: data.areaCode,
          phoneNumber: data.phoneNumber,
          isEmailNotificationEnabled: data.isEmailNotificationEnabled,
        };

        if (data.fileChanged) {
          payloadToSave.profilePhoto = base64Photo || '';
          payloadToSave.fileExtension = fileExt;
        }

        await updateProfileGeneralDataAsync(payloadToSave);

        setUsername(`${data.firstName} ${data.lastName}`);
        setJobTitle(data.jobTitle);
        if (data.fileChanged && base64Photo) {
          setProfilePicture(base64Photo);
        }

        setPristineData({ ...data, fileChanged: false });
        setData((prev) => ({ ...prev, fileChanged: false }));
        return true;
      } catch (error) {
        const message =
          error.response?.data?.message || 'Failed to update profile settings';
        setApiError(message);
        return false;
      }
    },
    reset: () => {
      if (pristineData) {
        setData(pristineData);
        setErrors({});
        setApiError('');
      }
    },
  }));

  const handleChange = (field, value) => {
    let fileExtension = data.fileExtension;
    let fileChanged = data.fileChanged;
    let safeValue = value;

    if (field === 'profilePhoto') {
      fileExtension = value?.name?.split('.').pop()?.toLowerCase() || '';
      fileChanged = true;
      safeValue = value;
    } else if (value && typeof value === 'object') {
      if (value.value !== undefined) safeValue = value.value;
      else if (value.target !== undefined) safeValue = value.target.value;
    }

    setData((prev) => ({
      ...prev,
      [field]: safeValue,
      fileExtension,
      fileChanged,
    }));
    setIsDirty(true);
    setErrors((prev) => ({ ...prev, [field]: '' }));
    setApiError('');
  };

  if (isLoading) return <BootstrapSpinner pushTop="30px" />;

  return (
    <div className={clsx(styles.generalForm, 'container')}>
      <section className={styles.imageSection}>
        <p className={styles.sectionTitle}>Profile Photo</p>
        <div className="row gx-2 mt-2">
          <div className="col col-12 col-lg-6">
            <OpImageUploader
              onChange={(val) => handleChange('profilePhoto', val)}
              value={data.profilePhoto}
            />
            {errors.profilePhoto && (
              <span
                className="text-danger mt-1 d-block"
                style={{ fontSize: '12px' }}
              >
                {errors.profilePhoto}
              </span>
            )}
          </div>
        </div>
      </section>

      <section className={styles.personalDetailsSection}>
        <p className={styles.sectionTitle}>Personal Details</p>
        <div className="row gx-2 mb-3">
          <div className="col-12 col-md-6 mt-3">
            <OpTextBox
              errorMessage={errors.firstName}
              label="First Name"
              onChange={(val) => handleChange('firstName', val)}
              showAsterisk
              value={data.firstName}
            />
          </div>
          <div className="col-12 col-md-6 mt-3">
            <OpTextBox
              errorMessage={errors.lastName}
              label="Last Name"
              onChange={(val) => handleChange('lastName', val)}
              showAsterisk
              value={data.lastName}
            />
          </div>
          <div className="col-12 col-md-6 mt-3">
            <OpTextBox
              errorMessage={errors.email}
              label="Email"
              onChange={(val) => handleChange('email', val)}
              showAsterisk
              value={data.email}
            />
          </div>
          <div className="col-12 col-md-6 mt-3">
            <OpTextBox
              label="Job Title"
              onChange={(val) => handleChange('jobTitle', val)}
              value={data.jobTitle}
            />
          </div>
          <div className="col-12 col-md-6 mt-3">
            <OpTextBox
              errorMessage={errors.orderContactRole}
              label="Order Contact Role"
              onChange={(val) => handleChange('orderContactRole', val)}
              showAsterisk
              value={data.orderContactRole}
            />
          </div>
        </div>
      </section>

      <section className={styles.phoneSection}>
        <p className={styles.sectionTitle}>Phone</p>
        <div className="row mb-4 gx-2 mt-3">
          <div className="col-12 col-md-3">
            <label className={styles.phoneLocationLabel}>Phone Location</label>
            <OpSelect
              dataSource={PhoneLocationOptions}
              fields={phoneLocationFields}
              onChange={(val) => handleChange('phoneLocation', val)}
              value={data.phoneLocation}
            />
          </div>
          <div className="col-12 col-md-3">
            <label className={styles.phoneTypeLabel}>Phone Type</label>
            <OpSelect
              dataSource={PhoneTypeOptions}
              fields={phoneTypeFields}
              onChange={(val) => handleChange('phoneType', val)}
              value={data.phoneType}
            />
          </div>
          <div className="col-12 col-md-3">
            <OpTextBox
              enableNumbersOnly
              errorMessage={errors.areaCode}
              label="Area Code"
              onChange={(val) => handleChange('areaCode', val)}
              showAsterisk
              value={data.areaCode}
            />
          </div>
          <div className="col-12 col-md-3">
            <OpTextBox
              enableNumbersOnly
              errorMessage={errors.phoneNumber}
              label="Phone Number"
              onChange={(val) => handleChange('phoneNumber', val)}
              showAsterisk
              value={data.phoneNumber}
            />
          </div>
        </div>
      </section>

      <section className={styles.notificationSection}>
        <p className={styles.sectionTitle}>Notifications</p>
        <div className="row gx-2">
          <div className="col-12">
            <Switch
              active={data.isEmailNotificationEnabled}
              label="Email notifications"
              onSwitch={(val) =>
                handleChange('isEmailNotificationEnabled', val)
              }
            />
          </div>
        </div>
      </section>

      {apiError && (
        <Modal
          children={
            <p
              className="content-emphasized"
              style={{ fontSize: '16px', textAlign: 'center' }}
            >
              {apiError}
            </p>
          }
          handleOnClose={() => setApiError('')}
        />
      )}
    </div>
  );
});

export default GeneralForm;
